import "../../styles/programme-management.css";
import DeleteProgrammeModal from "../../components/admin/DeleteProgrammeModal";
import NewProgrammeModal from "../../components/admin/NewProgrammeModal";
import AddProgrammeLauncher from "../../components/admin/AddProgrammeLauncher";
import UploadProgrammeWizard from "../../components/admin/UploadProgrammeWizard";
import { useEffect, useState } from "react";
import EditProgrammeModal from "../../components/admin/EditProgrammeModal";
import AdminLayout from "../../components/admin/AdminLayout";
import { useSearchParams } from "react-router-dom";
import {
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  CircleX,
  Eye,
  EyeOff,
  FileUp,
  Pencil,
  Plus,
  Search,
  Settings,
  Trash2
} from "lucide-react";

const API_BASE_URL =
  process.env.NODE_ENV === "development"
    ? "http://localhost:5000"
    : process.env.REACT_APP_API_BASE_URL ||
  "https://apti-backend-8fhm.onrender.com";

const ADMIN_API_KEY = process.env.REACT_APP_ADMIN_API_KEY;

const PROGRAMME_SETUP_COMPONENTS = [
  {
    key: "programmeIntelligence",
    label: "Programme Information"
  },
  {
    key: "behaviouralRequirements",
    label: "Behavioural Requirements"
  },
  {
    key: "programmeBehaviouralModel",
    label: "Programme Behavioural Model"
  },
  {
    key: "behaviourProfile",
    label: "Behaviour Profile"
  },
  {
    key: "fitExplanations",
    label: "Fit Explanations"
  },
  {
    key: "applicationNarratives",
    label: "Application Narratives"
  },
  {
    key: "fitDimensions",
    label: "Fit Dimensions"
  },
  {
    key: "environmentTraits",
    label: "Environment Traits"
  },
  {
    key: "learningEnvironment",
    label: "Learning Environment"
  },
  {
    key: "semanticTypes",
    label: "Semantic Types"
  },
  {
    key: "skillsToStrengthen",
    label: "Skills to Strengthen"
  },
  {
    key: "explanationBlueprint",
    label: "Explanation Blueprint"
  },
  {
    key: "behaviouralStory",
    label: "Behavioural Story"
  },
  {
    key: "fitInterpretation",
    label: "Fit Interpretation"
  },
  {
    key: "futureOutlook",
    label: "Future Outlook"
  },
  {
    key: "behaviouralNarrative",
    label: "Behavioural Narrative"
  }
];  

export default function ProgrammeManagement() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [programmes, setProgrammes] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isNewProgrammeModalOpen, setIsNewProgrammeModalOpen] = useState(false);
  const [isAddProgrammeModalOpen, setIsAddProgrammeModalOpen] = useState(false);
  const [isUploadWizardOpen, setIsUploadWizardOpen] = useState(false);
  const [sessionDuplicateCount, setSessionDuplicateCount] = useState(0);


  const [selectedProgramme, setSelectedProgramme] = useState(null);

  const PROGRAMMES_PER_PAGE = 5;

  useEffect(() => {
    if (searchParams.get("add") === "true") {
      setIsAddProgrammeModalOpen(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const isAddProgrammeFlowOpen =
    isAddProgrammeModalOpen ||
    isNewProgrammeModalOpen ||
    isUploadWizardOpen;

  useEffect(() => {
    if (!isAddProgrammeFlowOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    const previousPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;

    document.body.style.overflow = "hidden";

    if (scrollbarWidth > 0) {
      const currentPaddingRight =
        Number.parseFloat(window.getComputedStyle(document.body).paddingRight) || 0;
      document.body.style.paddingRight =
        `${currentPaddingRight + scrollbarWidth}px`;
    }

    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.paddingRight = previousPaddingRight;
    };
  }, [isAddProgrammeFlowOpen]);

  useEffect(() => {
    fetch(
      `${API_BASE_URL}/api/programs?api_key=${ADMIN_API_KEY}`
    )
      .then((res) => res.json())
      .then((data) => {

        setProgrammes(data.data || []);

      })
      .catch((err) => {
        console.error("Failed to load programmes:", err);
      });
  }, []);

    useEffect(() => {

    const initializingProgrammes =
      programmes.filter(
        (programme) =>
          programme.setup_status === "initializing"
      );

    if (initializingProgrammes.length === 0) {
      return;
    }

    let cancelled = false;

    const pollSetupStatus = async () => {

      try {

        const updates = await Promise.all(
          initializingProgrammes.map(async (programme) => {

            const response = await fetch(
              `${API_BASE_URL}/api/programs/${programme.id}/setup-status?api_key=${ADMIN_API_KEY}`
            );

            if (!response.ok) {
              return null;
            }

            const result = await response.json();

            return {
              id: programme.id,
              ...result
            };

          })
        );

        if (cancelled) {
          return;
        }

        setProgrammes((current) =>
          current.map((programme) => {

            const update = updates.find(
              (item) =>
                item &&
                item.id === programme.id
            );

            if (!update) {
              return programme;
            }

            return {
              ...programme,
              setup_status:
                update.setup_status,
              setup_progress:
                update.setup_progress,
              is_active:
                update.is_active
            };

          })
        );

      } catch (error) {

        console.error(
          "Failed to poll Programme Setup:",
          error
        );

      }

    };

    const timer = setTimeout(
      pollSetupStatus,
      1000
    );

    return () => {

      cancelled = true;
      clearTimeout(timer);

    };

  }, [programmes]);

  const filteredProgrammes = programmes.filter((programme) => {
    const matchesSearch =
      programme.program_name
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "all" ||
      (statusFilter === "enabled" && programme.is_active) ||
      (statusFilter === "disabled" && !programme.is_active);

    return matchesSearch && matchesStatus;
  });

  const totalPages = Math.ceil(
    filteredProgrammes.length / PROGRAMMES_PER_PAGE
  );

  const startIndex =
    (currentPage - 1) * PROGRAMMES_PER_PAGE;

  const paginatedProgrammes =
    filteredProgrammes.slice(
      startIndex,
      startIndex + PROGRAMMES_PER_PAGE
    );

  const handleAddProgramme = async (programmeData) => {

  try {

    const programmesUrl = `${API_BASE_URL}/api/programs`;

    const response = await fetch(
      programmesUrl,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          ...programmeData,
          api_key:
            ADMIN_API_KEY
        })
      }
    );

    const contentType = response.headers.get("content-type") || "";
    const responseBody = await response.text();


    if (!contentType.includes("application/json")) {
      throw new Error(
        `Programme API returned ${contentType || "an unknown content type"} instead of JSON.`
      );
    }

    const result = JSON.parse(responseBody);

    if (!response.ok) {
      if (result.code === "DUPLICATE_PROGRAMME") {
        setSessionDuplicateCount((count) => count + 1);
      }
      throw new Error(result.error);
    }

    setProgrammes((current) => [
      ...current,
      result.data
    ]);

    setIsNewProgrammeModalOpen(false);

  } catch (error) {

    console.error(
      "Failed to add programme:",
      error
    );

    alert(error.message || "Unable to create programme.");

  }

};

  const handleDeleteProgramme = (programme) => {

    setSelectedProgramme(programme);

    setIsDeleteModalOpen(true);

  };

  const confirmDeleteProgramme = async () => {

    if (!selectedProgramme) return;

    try {

      const response = await fetch(
        `${API_BASE_URL}/api/programs/${selectedProgramme.id}?api_key=${ADMIN_API_KEY}`,
        {
          method: "DELETE",
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error);
      }

      setProgrammes((current) =>
        current.filter(
          (p) => p.id !== selectedProgramme.id
        )
      );

      setIsDeleteModalOpen(false);

      setSelectedProgramme(null);

    } catch (error) {

      console.error(
        "Failed to delete programme:",
        error
      );

      alert("Unable to delete programme.");

    }

  };

  const handleSaveProgramme = async (updatedProgramme) => {

    try {

      const response = await fetch(
        `${API_BASE_URL}/api/programs/${updatedProgramme.id}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            api_key: ADMIN_API_KEY,
            program_name: updatedProgramme.program_name,
            degree: updatedProgramme.degree,
            apply_url: updatedProgramme.apply_url
          })
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error);
      }

      setProgrammes((current) =>
        current.map((programme) =>
          programme.id === result.data.id
            ? result.data
            : programme
        )
      );

      setIsEditModalOpen(false);

      setSelectedProgramme(null);

    } catch (error) {

        console.error(
          "Failed to update programme:",
          error
        );

        alert(error.message);

    }

  };
  const handleToggleProgramme = async (programme) => {

    try {

        const response = await fetch(
            `${API_BASE_URL}/api/programs/${programme.id}/toggle?api_key=${ADMIN_API_KEY}`,
            {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                },
            }
        );

        const result = await response.json();

        if (!response.ok) {
            throw new Error(result.error || "Unable to update programme.");
        }

        if (result.success) {

            setProgrammes((current) =>
                current.map((item) =>
                    item.id === programme.id
                        ? result.data
                        : item
                )
            );

        }

    } catch (error) {

        console.error("Failed to update programme:", error);

    }

  };

  const handleViewProgramme = (programme) => {
    if (programme.apply_url) {
      window.open(programme.apply_url, "_blank", "noopener,noreferrer");
    }
  };

  const getCompletedCheckCount = (programme) =>
    PROGRAMME_SETUP_COMPONENTS.filter((component) => {
      const status = programme.setup_progress?.[component.key];
      return status === "checked" || status === "missing";
    }).length;

  const initializingProgrammes = programmes.filter(
    (programme) => programme.setup_status === "initializing"
  );
  const completeCount = programmes.filter(
    (programme) => programme.setup_status === "complete"
  ).length;
  const needsReviewCount = programmes.filter(
    (programme) => programme.setup_status === "needs_review"
  ).length;
  const processedCount = completeCount + needsReviewCount;
  const aggregateCompletedChecks = initializingProgrammes.reduce(
    (total, programme) => total + getCompletedCheckCount(programme),
    0
  );
  const coverageDenominator = completeCount + needsReviewCount;
  const intelligenceCoveragePercentage =
    coverageDenominator > 0
      ? Math.round(
          (completeCount / coverageDenominator) * 100
        )
      : 0;

  const reviewGuidance = {
    information_required:
      "Provide the programme synopsis, course list and available curriculum documents.",
    documents_submitted:
      "Documents submitted. Awaiting AptiGuide review.",
    under_review:
      "Documents are currently under AptiGuide review.",
    review_complete:
      "AptiGuide review complete. Programme information updated."
  };
  const currentReviewState =
    needsReviewCount > 0
      ? "information_required"
      : "review_complete";
  const currentReviewGuidance =
    reviewGuidance[currentReviewState];

  const activeStage =
    isAddProgrammeModalOpen ||
    isNewProgrammeModalOpen ||
    isUploadWizardOpen ||
    programmes.length === 0
      ? 1
      : initializingProgrammes.length && aggregateCompletedChecks === 0
      ? 2
      : initializingProgrammes.length
      ? 3
      : 4;

  const workflowStages = [
    {
      title: "Add Programme",
      description: "Add a single programme or upload catalogue",
      icon: FileUp
    },
    {
      title: "Programme Information Check",
      description: "AptiGuide checks programme information and duplicates",
      icon: Search
    },
    {
      title: "Programme Setup",
      description: "AptiGuide completes programme setup automatically",
      icon: Settings
    },
    {
      title: "Finish",
      description: "Programme is complete or needs review",
      icon: Check
    }
  ];

  const formatDateAdded = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    return Number.isNaN(date.getTime())
      ? "—"
      : date.toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
          year: "numeric"
        });
  };

  const progressStateFor = (programme) => {
    const count = getCompletedCheckCount(programme);
    if (programme.setup_status === "complete") {
      return { count: 16, label: "Complete", tone: "complete" };
    }
    if (programme.setup_status === "initializing") {
      return { count, label: "Initializing", tone: "initializing" };
    }
    return { count, label: "Needs Review", tone: "review" };
  };

  return (
      <AdminLayout
        title="Programme Management"
        subtitle="Manage your university's programmes and their programme setup"
      >
        <div className="pm-redesign">
          <section className="pm-workflow" aria-label="Programme setup workflow">
            {workflowStages.map((stage, index) => {
              const StageIcon = stage.icon;
              const stageNumber = index + 1;
              const state =
                stageNumber < activeStage
                  ? "complete"
                  : stageNumber === activeStage
                  ? "active"
                  : "future";
              return (
                <div className={`pm-stage pm-stage--${state}`} key={stage.title}>
                  <div className="pm-stage-visual">
                    <span className="pm-stage-number">{stageNumber}</span>
                    <span className="pm-stage-icon"><StageIcon size={20} /></span>
                    {index < workflowStages.length - 1 && <span className="pm-stage-line" />}
                  </div>
                  <strong>{stage.title}</strong>
                  <span>{stage.description}</span>
                </div>
              );
            })}
          </section>

          <section className="pm-insights">
            <article className="pm-card pm-progress-card">
              <header><h2>Programme Information Check</h2><span className="pm-state-badge is-coverage">Current Setup</span></header>
              <div className={`pm-progress-ring ${coverageDenominator === 0 ? "is-empty" : ""}`} style={{ "--pm-coverage": `${intelligenceCoveragePercentage * 3.6}deg` }}>
                <div><strong>{intelligenceCoveragePercentage}%</strong><span>Programme setup</span></div>
              </div>
              <p>{coverageDenominator > 0 ? `${completeCount} of ${coverageDenominator} programme${coverageDenominator === 1 ? "" : "s"} ${coverageDenominator === 1 ? "has" : "have"} complete programme setup` : "No programme setup has been evaluated yet"}</p>
              {coverageDenominator > 0 && <small>{needsReviewCount > 0 ? `${needsReviewCount} programme${needsReviewCount === 1 ? "" : "s"} still need AptiGuide review` : "All evaluated programmes have complete setup"}</small>}
              {initializingProgrammes.length > 0 && <small>Programme setup will update as checks finish</small>}
            </article>

            <article className="pm-card pm-summary-card">
              <header><h2>Check Summary</h2></header>
              <div className="pm-summary-row success"><CheckCircle2 /><span><strong>Setup Complete</strong><small>Programmes with complete setup</small></span><b>{completeCount}</b></div>
              <div className="pm-summary-row warning"><CircleAlert /><span><strong>Needs Review</strong><small>Programmes requiring additional information</small></span><b>{needsReviewCount}</b></div>
              <div className="pm-summary-row danger"><CircleX /><span><strong>Duplicates Detected</strong><small>Single-programme duplicates rejected this session</small></span><b>{sessionDuplicateCount}</b></div>
              <div className="pm-summary-total"><strong>Total Processed</strong><b>{processedCount}</b></div>
            </article>

            <article className="pm-card pm-next-card">
              <header><h2>What Happens Next?</h2></header>
              <div className="pm-next-row success"><CheckCircle2 /><span><strong>{completeCount} programme{completeCount === 1 ? "" : "s"} set up automatically</strong><small>AptiGuide configured and validated all required components.</small></span></div>
              <div className="pm-next-row warning" aria-live="polite" data-review-state={currentReviewState}><CircleAlert /><span><strong>{needsReviewCount > 0 ? `${needsReviewCount} programme${needsReviewCount === 1 ? "" : "s"} need review` : "AptiGuide review complete"}</strong><small>{currentReviewGuidance}</small></span></div>
              <div className="pm-next-row danger"><CircleX /><span><strong>{sessionDuplicateCount} duplicate{sessionDuplicateCount === 1 ? "" : "s"} excluded</strong><small>These programmes already existed and were not added.</small></span></div>
              <div className="pm-live-notice"><CircleAlert /><span><strong>You can continue working while setup runs.</strong><small>We will update statuses in real time.</small></span></div>
            </article>
          </section>

          <section className="pm-card pm-table-card">
            <header className="pm-table-toolbar">
              <h2>Programme Management</h2>
              <label className="pm-search"><Search size={15} /><input value={searchTerm} onChange={(event) => { setSearchTerm(event.target.value); setCurrentPage(1); }} placeholder="Search programmes..." /></label>
              <select value={statusFilter} onChange={(event) => { setStatusFilter(event.target.value); setCurrentPage(1); }} aria-label="Filter programmes by status">
                <option value="all">All Statuses</option><option value="enabled">Enabled</option><option value="disabled">Disabled</option>
              </select>
              <button className="pm-add-button" onClick={() => setIsAddProgrammeModalOpen(true)}><Plus size={16} />Add Programmes<span className="pm-add-caret">⌄</span></button>
            </header>

            <div className="pm-table-scroll">
              <table className="pm-table">
                <thead><tr><th>Programme Name</th><th>Degree</th><th>Programme Setup</th><th>Status</th><th>Date Added</th><th>Actions</th></tr></thead>
                <tbody>
                  {paginatedProgrammes.map((programme) => {
                    const progress = progressStateFor(programme);
                    return <tr key={programme.id}>
                      <td>{programme.program_name}</td>
                      <td>{programme.degree || "—"}</td>
                      <td><div className={`pm-row-progress pm-row-progress--${progress.tone}`}><div><span style={{ width: `${Math.round((progress.count / 16) * 100)}%` }} /></div><p><span>{progress.label}</span><b>{progress.count} / 16</b></p></div></td>
                      <td><span className={`pm-status ${programme.is_active ? "enabled" : "disabled"}`}><i />{programme.is_active ? "Enabled" : "Disabled"}</span></td>
                      <td>{formatDateAdded(programme.created_at)}</td>
                      <td><div className="pm-actions">
                        <button onClick={() => handleViewProgramme(programme)} disabled={!programme.apply_url} title={programme.apply_url ? "View programme application page" : "Application page unavailable"} aria-label={`View ${programme.program_name}`}><Eye size={15} /></button>
                        <button onClick={() => { setSelectedProgramme(programme); setIsEditModalOpen(true); }} title="Edit programme" aria-label={`Edit ${programme.program_name}`}><Pencil size={15} /></button>
                        <button onClick={() => handleToggleProgramme(programme)} disabled={!programme.is_active && programme.setup_status !== "complete"} title={programme.is_active ? "Disable programme" : programme.setup_status === "complete" ? "Enable programme" : "Programme information required"} aria-label={`${programme.is_active ? "Disable" : "Enable"} ${programme.program_name}`}>{programme.is_active ? <EyeOff size={15} /> : <Eye size={15} />}</button>
                        <button className="danger" onClick={() => handleDeleteProgramme(programme)} title="Delete programme" aria-label={`Delete ${programme.program_name}`}><Trash2 size={15} /></button>
                      </div></td>
                    </tr>;
                  })}
                </tbody>
              </table>
              {paginatedProgrammes.length === 0 && <div className="pm-empty">No programmes match the current filters.</div>}
            </div>

            <footer className="pm-pagination">
              <span>Showing {filteredProgrammes.length ? startIndex + 1 : 0} to {Math.min(startIndex + paginatedProgrammes.length, filteredProgrammes.length)} of {filteredProgrammes.length} programmes</span>
              <div><button disabled={currentPage === 1} onClick={() => setCurrentPage((page) => page - 1)}><ChevronLeft size={14} />Previous</button>{Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => <button className={page === currentPage ? "active" : ""} key={page} onClick={() => setCurrentPage(page)}>{page}</button>)}<button disabled={currentPage === totalPages || totalPages === 0} onClick={() => setCurrentPage((page) => page + 1)}>Next<ChevronRight size={14} /></button></div>
            </footer>
          </section>

          <EditProgrammeModal isOpen={isEditModalOpen} programme={selectedProgramme} onClose={() => { setIsEditModalOpen(false); setSelectedProgramme(null); }} onSave={handleSaveProgramme} />
          <DeleteProgrammeModal isOpen={isDeleteModalOpen} programmeName={selectedProgramme?.program_name} onCancel={() => { setIsDeleteModalOpen(false); setSelectedProgramme(null); }} onConfirm={confirmDeleteProgramme} />
          <AddProgrammeLauncher isOpen={isAddProgrammeModalOpen} onClose={() => setIsAddProgrammeModalOpen(false)} onSingleProgramme={() => { setIsAddProgrammeModalOpen(false); setIsNewProgrammeModalOpen(true); }} onUploadCatalogue={() => { setIsAddProgrammeModalOpen(false); setIsUploadWizardOpen(true); }} />
          <UploadProgrammeWizard isOpen={isUploadWizardOpen} onClose={() => setIsUploadWizardOpen(false)} existingProgrammes={programmes} />
          <NewProgrammeModal isOpen={isNewProgrammeModalOpen} onClose={() => setIsNewProgrammeModalOpen(false)} onAdd={handleAddProgramme} />
        </div>
      </AdminLayout>
    );

}
      
